import { useEffect, useRef, useState } from 'react';

type PlayerStatus = 'idle' | 'loading' | 'playing' | 'error';

interface DemoPlayerProps {
  poster: string;
  title: string;
  episode: number;
}

export default function DemoPlayer({ poster, title, episode }: DemoPlayerProps) {
  const [status, setStatus] = useState<PlayerStatus>('idle');
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const start = (simulateError = false) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(0);
    setStatus('loading');
    window.setTimeout(() => {
      if (simulateError) {
        setStatus('error');
        return;
      }
      setStatus('playing');
      timerRef.current = setInterval(() => {
        setProgress((p) => {
          if (p >= 100) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 100;
          }
          return p + 0.6;
        });
      }, 60);
    }, 1200);
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950">
        <img
          src={poster}
          alt={`第${episode}集 ${title} 播放演示封面`}
          title={`善良的小姨子 第${episode}集 在线观看（演示）`}
          className="w-full h-full object-cover object-top opacity-70"
        />

        {(status === 'idle' || status === 'error') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-950/45 backdrop-blur-[2px] px-6 text-center">
            {status === 'error' ? (
              <>
                <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-50/10 border border-background-50/30 text-background-50 mb-4">
                  <i className="ri-error-warning-line text-3xl"></i>
                </span>
                <p className="text-background-50 font-medium mb-1.5">播放失败（演示状态）</p>
                <p className="text-background-300 text-sm mb-5 max-w-md">
                  本播放器为前端演示，不接入真实视频源。该状态用于演示异常提示。
                </p>
                <button
                  type="button"
                  onClick={() => start(false)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                  重新加载
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => start(false)}
                  aria-label="开始演示播放"
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 text-primary-950 flex items-center justify-center hover:bg-accent-400 transition-colors mb-4 cursor-pointer"
                >
                  <i className="ri-play-fill text-3xl md:text-4xl"></i>
                </button>
                <p className="text-background-50 font-medium text-sm md:text-base">
                  第{episode}集 · {title}
                </p>
                <p className="text-background-300 text-xs md:text-sm mt-1">
                  点击播放按钮体验前端播放演示
                </p>
              </>
            )}
          </div>
        )}

        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-950/60">
            <span className="w-10 h-10 rounded-full border-2 border-background-50/30 border-t-accent-500 animate-spin mb-3"></span>
            <p className="text-background-50 text-sm">正在加载演示资源…</p>
          </div>
        )}

        {status === 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-950/55">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-50/10 border border-background-50/30 text-background-50 mb-3">
              <i className="ri-equalizer-line text-2xl"></i>
            </span>
            <p className="text-background-50 text-sm">演示播放中（非真实视频）</p>
          </div>
        )}

        <div className="absolute left-0 right-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-primary-950/90 to-transparent">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="播放或重播"
              onClick={() => start(false)}
              className="w-9 h-9 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer shrink-0"
            >
              <i className={status === 'playing' ? 'ri-pause-fill text-lg' : 'ri-play-fill text-lg'}></i>
            </button>
            <div className="flex-1 h-1.5 rounded-full bg-background-50/20 overflow-hidden">
              <div
                className="h-full bg-accent-500 transition-all"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="text-background-200 text-xs font-label shrink-0">
              {Math.floor(progress)}%
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-accent-50 border border-accent-200 rounded-lg px-4 py-3">
        <p className="text-xs md:text-sm text-accent-900 flex items-start sm:items-center gap-2">
          <i className="ri-information-line w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0"></i>
          本视频区域为前端播放演示，仅示意播放状态，不提供真实视频源、下载或会员服务。
        </p>
        <button
          type="button"
          onClick={() => start(true)}
          className="text-xs text-accent-800 underline underline-offset-2 hover:text-accent-600 whitespace-nowrap cursor-pointer shrink-0"
        >
          演示播放失败状态
        </button>
      </div>
    </div>
  );
}